/**
 * MySQL text expressions independent of `NO_BACKSLASH_ESCAPES`.
 *
 * MySQL is the only supported engine whose string-literal *lexing* depends on
 * a session setting: without `NO_BACKSLASH_ESCAPES` a `\` inside `'…'` escapes
 * the next character (`C:\new\test` becomes a string holding a newline and a
 * tab), and `a\'` swallows the closing quote entirely. Quote-doubling alone —
 * `escapeSqlLiteral` in `sqlLiteral.ts` — is therefore wrong on MySQL, which
 * is why that helper carries a MySQL warning and these live here instead.
 *
 * WHY THE HEX FORM CARRIES A `_utf8mb4` INTRODUCER AND NOT `CONVERT(… USING …)`
 * ---------------------------------------------------------------------------
 * Both spell the same bytes, but they differ in *collation coercibility*, and
 * that decides whether a comparison runs at all:
 *
 *   COERCIBILITY(CONVERT(X'…' USING utf8mb4)) = 2  (IMPLICIT — like a column)
 *   COERCIBILITY(_utf8mb4 X'…')               = 4  (COERCIBLE — like a literal)
 *
 * An IMPLICIT operand does not yield to the column's collation, so comparing
 * `CONVERT(…)` against a `utf8mb4_unicode_ci` / `utf8mb4_general_ci` column
 * (both extremely common; the server default on MySQL 8 is
 * `utf8mb4_0900_ai_ci`) raises `ER_CANT_AGGREGATE_2COLLATIONS` — "Illegal mix
 * of collations" — instead of returning rows. Reproduced live on MySQL 8.0.46
 * for `=` and for `LIKE … ESCAPE …`. The introducer form has a literal's
 * coercibility, so it adopts the other operand's collation exactly like the
 * `'…'` branch below, and was verified live against `utf8mb4_unicode_ci`,
 * `utf8mb4_general_ci`, `utf8mb4_bin`, `latin1_swedish_ci` and
 * `utf8mb3_general_ci` columns under `''`, `NO_BACKSLASH_ESCAPES` and
 * `ANSI_QUOTES,NO_BACKSLASH_ESCAPES`.
 *
 * The one place `CONVERT(… USING utf8mb4)` is still required is a literal that
 * MySQL *persists and re-parses* — a stored CHECK expression. MySQL
 * re-serializes `_utf8mb4 X'…'` back into a quoted literal in
 * `CHECK_CONSTRAINTS.CHECK_CLAUSE`, so the constraint then evaluates under
 * whatever mode reads it (verified: a `_utf8mb4` CHECK created under
 * `NO_BACKSLASH_ESCAPES` rejects the very value it was built from), whereas
 * `convert(0x… using utf8mb4)` survives verbatim. Those call sites build the
 * `CONVERT` form explicitly and say why.
 */

/** A MySQL text literal that preserves `value` byte-for-byte in every SQL
 *  mode. Use for any generated comparand, JSON path, or concatenated SQL
 *  fragment. Not usable where the grammar demands a quoted string token
 *  (`IDENTIFIED BY`) — use `quoteMysqlString` there. */
export function quoteMysqlText(value: string): string {
  if (!/[\\\0]/u.test(value)) return `'${value.replace(/'/g, "''")}'`;
  const hex = Array.from(new TextEncoder().encode(value), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `_utf8mb4 X'${hex}'`;
}

/** DDL password positions require a quoted string, not an expression/bind. */
export function quoteMysqlString(value: string, noBackslashEscapes = false): string {
  const text = noBackslashEscapes ? value : value.replace(/\\/g, '\\\\').replace(/\0/g, '\\0');
  return `'${text.replace(/'/g, "''")}'`;
}

/** Account names accept identifier quoting, which has no backslash escapes. */
export function quoteMysqlPrincipal(name: string, host = '%'): string {
  const quote = (part: string) => /[\\\0]/u.test(part)
    ? `\`${part.replace(/`/g, '``')}\``
    : `'${part.replace(/'/g, "''")}'`;
  return `${quote(name)}@${quote(host)}`;
}
